import React, { useState, useMemo } from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  type FieldErrors,
  type UseFieldArrayReturn,
  type UseFormSetValue,
  type FieldArrayWithId,
  type DefaultValues,
  type Resolver,
  type Control,
  type UseFieldArrayRemove,
  type UseFormWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  Box,
  Typography,
  Autocomplete,
  IconButton,
  Collapse,
  Icon,
  FormControlLabel,
  Switch,
  Paper,
  List,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  DialogProps,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import {
  Close as CloseIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  RMA_FULL,
  RMAFormMode,
  RMAReasonType,
  RMAReasonOption,
  RMA_REASON_OPTIONS,
  RMAStatusType,
  RMACFOption,
  RMACustOption,
  RMAItemOption,
  RMALotOption,
  RMACMOption,
  RMAShipmentOption,
  RMAOption,
  RMAFormDataBatchCreateEntry,
  RMAFormDataBatchCreateGroup,
  RMAFormDataBatchCreateFull,
  RMAValidationSchemaBatchCreateFull,
  RMAFormDataReceiveBatch,
  RMAValidationSchemaReceiveFull,
  RMAFormDataUpdate,
  RMAValidationSchemaUpdate,
  RMAFormDataCreate,
  RMAValidationSchemaCreate,
  RMAFormDataReceive,
  RMAValidationSchemaReceive,
} from "../../types/qualityRecordTypes";
import { AuthUser } from "../../services/svc_auth";
import stringUtils from "../../utils/stringUtils";
import { NumericEntryField } from "../../components/common/CustomFields";
import { AddressField } from "../../components/common/AddressViewEdit";
import { BlankOrderAddressObject } from "../../types/common/baseTableTypes";
import { WorldLocationProvider } from "../../contexts/WorldLocationContextProvider";
import ConflictNotificationBadge from "../../components/common/ConflictNotificationBadge";
import ProgressIndicator, {
  ProgressIndicatorProps,
} from "../../components/common/ProgressIndicator";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface RMADialogShellProps {
  open: boolean;
  maxWidth?: DialogProps["maxWidth"];
  mode: RMAFormMode;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  onClearError: () => void;
  onClose: () => void;
  debugValues?: unknown;
  debugErrors?: unknown;
  children: React.ReactNode; // form body
  actions: React.ReactNode; // footer buttons (already wired to handleSubmit)
  user: AuthUser | null;
}

const RMADialogShell: React.FC<RMADialogShellProps> = ({
  open,
  maxWidth = "md",
  mode,
  isSubmitting,
  error,
  success,
  onClearError,
  onClose,
  debugValues,
  debugErrors,
  children,
  actions,
  user,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    slotProps={{ paper: { sx: { minHeight: 600 } } }}
  >
    <DialogTitle>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          {stringUtils.capFirstLetter(mode)} RMA
        </Typography>
        <Box display="flex" gap={1}>
          {user?.is_superuser && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => {
                console.group("RMA Dialog Debug");
                console.log("values", debugValues);
                console.log("errors", debugErrors);
                console.groupEnd();
              }}
            >
              DEBUG
            </Button>
          )}
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Stack>
    </DialogTitle>

    <DialogContent>
      <WorldLocationProvider>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {success && <Alert severity="success">{success}</Alert>}
          {error && (
            <Alert severity="error" onClose={onClearError}>
              {error}
            </Alert>
          )}
          {open && children}
        </Stack>
      </WorldLocationProvider>
    </DialogContent>

    <DialogActions>{actions}</DialogActions>
  </Dialog>
);

const RMABatchCreateRow: React.FC<{
  errors: FieldErrors<RMAFormDataBatchCreateEntry> | undefined;
  watch: UseFormWatch<RMAFormDataBatchCreateFull>;
  control: Control<RMAFormDataBatchCreateFull, any, RMAFormDataBatchCreateFull>;
  setValue: UseFormSetValue<RMAFormDataBatchCreateFull>;
  rowData: FieldArrayWithId<
    RMAFormDataBatchCreateFull,
    `rma_data.${number}.entries`,
    "id"
  >;
  groupIndex: number;
  rmaIndex: number;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  itemOptions: RMAItemOption[];
  lotOptions: RMALotOption[];
  reasonOptions: RMAReasonOption[];
  onLotSelected: (
    lotOpt: RMALotOption | null,
    rmaIndex: number,
    groupIndex: number,
  ) => void;
  lotConflicts: string[];
  onRemove: () => void;
}> = ({
  errors,
  watch,
  control,
  setValue,
  rowData,
  groupIndex,
  rmaIndex,
  activeIndex,
  setActiveIndex,
  itemOptions,
  lotOptions,
  reasonOptions,
  onLotSelected,
  lotConflicts,
  onRemove,
}) => {
  const isRowFocused: boolean = useMemo(() => {
    return activeIndex === rmaIndex;
  }, [activeIndex, rmaIndex]);
  const [watchedReasonOption] = watch([
    `rma_data.${groupIndex}.entries.${rmaIndex}.reason`,
  ]);

  const conflict: React.ReactNode = React.useMemo(() => {
    if (lotConflicts.length > 0) {
      return (
        <Stack
          direction="column"
          width="100%"
          spacing={0.5}
          alignContent={"start"}
        >
          <Typography variant="h6">Conflicts With:</Typography>
          {lotConflicts.map((c) => {
            const confSplit = c.split(".");
            return (
              <Typography variant="body1">{`Group #${parseInt(confSplit[0]) + 1} RMA #${parseInt(confSplit[1]) + 1}`}</Typography>
            );
          })}
        </Stack>
      );
    }
    return null;
  }, [lotConflicts]);

  return (
    <Box
      key={rowData.id}
      gap={1}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        alignItems: "center",
      }}
      display="flex"
      width="100%"
    >
      <Stack spacing={0.5} width="100%">
        <Box display="flex" width="100%">
          <Box
            display="flex"
            justifyContent="space-between"
            onClick={() => {
              setActiveIndex((prev) => {
                if (prev === rmaIndex) {
                  return -1;
                } else {
                  return rmaIndex;
                }
              });
            }}
            sx={{ p: 2, cursor: "pointer" }}
            width="100%"
          >
            <Box display="flex" gap={0.5}>
              <Typography variant="h4">New RMA #{rmaIndex + 1}</Typography>
              {!!errors && <Alert severity="error">Contains Errors</Alert>}
            </Box>
            <Icon>{isRowFocused ? <DownIcon /> : <UpIcon />}</Icon>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              console.log("CLICKED REMOVE RMA", {
                group: groupIndex,
                rma: rmaIndex,
              });
              onRemove();
            }}
          >
            Remove
          </Button>
        </Box>

        <Collapse
          in={isRowFocused}
          timeout="auto"
          unmountOnExit
          sx={{ width: "100%" }}
        >
          <Stack direction="column" spacing={1}>
            <Box display="flex" gap={1}>
              <Controller
                name={`rma_data.${groupIndex}.entries.${rmaIndex}.reason`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <Autocomplete
                    value={value}
                    onChange={(e, v) => {
                      onChange(v);
                      setValue(
                        `rma_data.${groupIndex}.entries.${rmaIndex}.reason_other`,
                        null,
                      );
                    }}
                    options={reasonOptions}
                    getOptionLabel={(opt) => opt?.label || ""}
                    renderInput={(params) => (
                      <TextField
                        label="Reason for Return"
                        {...params}
                        error={!!errors?.reason}
                        helperText={errors?.reason?.message}
                      />
                    )}
                    fullWidth
                    {...restField}
                  />
                )}
              />
              <Controller
                name={`rma_data.${groupIndex}.entries.${rmaIndex}.reason_other`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <TextField
                    label="Reason - Other Context"
                    value={value || ""}
                    onChange={onChange}
                    error={!!errors?.reason_other}
                    helperText={errors?.reason_other?.message}
                    {...restField}
                    fullWidth
                    disabled={watchedReasonOption?.label !== "Other"}
                  />
                )}
              />
            </Box>
            <Box display="flex" gap={1}>
              <Controller
                name={`rma_data.${groupIndex}.entries.${rmaIndex}.item`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <Autocomplete
                    value={value}
                    onChange={(e, v) => {
                      onChange(v);
                    }}
                    options={itemOptions}
                    getOptionLabel={(opt) => opt?.label || ""}
                    renderInput={(params) => (
                      <TextField
                        label="RMA Item"
                        {...params}
                        error={!!errors?.item}
                        helperText={errors?.item?.message}
                      />
                    )}
                    fullWidth
                    {...restField}
                  />
                )}
              />
              <Controller
                name={`rma_data.${groupIndex}.entries.${rmaIndex}.lot`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <Autocomplete
                    value={value}
                    onChange={(e, v) => {
                      onChange(v);
                      onLotSelected(v, rmaIndex, groupIndex);
                    }}
                    options={lotOptions}
                    getOptionLabel={(opt) => opt?.label || ""}
                    renderInput={(params) => {
                      const newAdorn = (
                        <Box display="flex">
                          {!!conflict && (
                            <ConflictNotificationBadge
                              id={`${groupIndex}-${rmaIndex}-conflict-notif-badge`}
                            >
                              {conflict}
                            </ConflictNotificationBadge>
                          )}

                          {params.InputProps.endAdornment}
                        </Box>
                      );
                      params.InputProps.endAdornment = newAdorn;
                      return (
                        <TextField
                          label="RMA Lot"
                          {...params}
                          error={!!errors?.lot}
                          helperText={errors?.lot?.message}
                        />
                      );
                    }}
                    fullWidth
                    {...restField}
                  />
                )}
              />
              <Controller
                name={`rma_data.${groupIndex}.entries.${rmaIndex}.quantity`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <NumericEntryField
                    value={value}
                    onChange={onChange}
                    label="Quantity Returning"
                    decimalPlaces={0}
                    isCurrency={false}
                    error={!!errors?.quantity}
                    helperText={errors?.quantity?.message}
                    textFieldProps={{ fullWidth: true }}
                  />
                )}
              />
            </Box>
            <Controller
              name={`rma_data.${groupIndex}.entries.${rmaIndex}.rma_details`} // fully typed path
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="RMA Details"
                  error={!!errors?.rma_details}
                  helperText={errors?.rma_details?.message}
                  fullWidth
                />
              )}
            />
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};

// An empty row, typed against the real field type. If this object is missing
// a field or has the wrong shape, you get a compile error HERE — which is
// exactly the enforcement you were chasing at the start.
function makeEmptyBatchRow(): RMAFormDataBatchCreateEntry {
  return {
    item: null,
    lot: null,
    reason: null,
    reason_other: null,
    rma_details: "",
    quantity: 1,
  };
}

const RMABatchCreateGroup: React.FC<{
  errors: FieldErrors<RMAFormDataBatchCreateGroup> | undefined;
  watch: UseFormWatch<RMAFormDataBatchCreateFull>;
  control: Control<RMAFormDataBatchCreateFull, any, RMAFormDataBatchCreateFull>;
  setValue: UseFormSetValue<RMAFormDataBatchCreateFull>;
  groupData: FieldArrayWithId<RMAFormDataBatchCreateFull, "rma_data", "id">;
  groupIndex: number;
  activeGroupIndex: number;
  setActiveGroupIndex: React.Dispatch<React.SetStateAction<number>>;
  custFeedbackOptions: RMACFOption[];
  custOptions: RMACustOption[];
  itemOptions: RMAItemOption[];
  lotOptions: RMALotOption[];
  reasonOptions: RMAReasonOption[];
  onLotSelected: (
    lotOpt: RMALotOption | null,
    rmaIndex: number,
    groupIndex: number,
  ) => void;
  groupConflicts: { lot: string | null; conflicts: string[] }[];
  setGroupConflicts: React.Dispatch<
    React.SetStateAction<
      {
        lot: string | null;
        conflicts: string[];
      }[][]
    >
  >;
  onRemoveGroup: () => void;
  onRemoveRMA: (rmaInd: number) => void;
}> = ({
  errors,
  watch,
  control,
  setValue,
  groupData,
  groupIndex,
  activeGroupIndex,
  setActiveGroupIndex,
  custFeedbackOptions,
  custOptions,
  itemOptions,
  lotOptions,
  reasonOptions,
  onLotSelected,
  groupConflicts,
  setGroupConflicts,
  onRemoveGroup,
  onRemoveRMA,
}) => {
  const [useAltReturn, setUseAltReturn] = useState<boolean>(false);
  const [activeRMAIndex, setActiveRMAIndex] = useState<number>(-1);

  const isGroupFocused = useMemo(() => {
    return groupIndex === activeGroupIndex;
  }, [groupIndex, activeGroupIndex]);

  const [
    watchedCFOption,
    watchedCustOption,
    watchedInitAddrObject,
    watchedReturnAddrObject,
  ] = watch([
    `rma_data.${groupIndex}.customer_feedback`,
    `rma_data.${groupIndex}.customer`,
    `rma_data.${groupIndex}.address`,
    `rma_data.${groupIndex}.return_addr`,
  ]);

  const { fields, remove, append } = useFieldArray({
    control,
    name: `rma_data.${groupIndex}.entries`,
  });

  return (
    <Stack
      key={`group-${groupData.id}`}
      spacing={1}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        alignItems: "center",
      }}
      direction="column"
      width="100%"
    >
      <Box display="flex" width="100%">
        <Box
          display="flex"
          onClick={() => {
            setActiveGroupIndex((prev) => {
              if (prev === groupIndex) {
                return -1;
              } else {
                return groupIndex;
              }
            });
          }}
          sx={{ p: 2, cursor: "pointer" }}
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mr="auto"
        >
          <Box display="flex" gap={1}>
            <Typography variant="h3">Group #{groupIndex + 1}</Typography>
            {!!errors && <Alert severity="error">{"Contains Errors"}</Alert>}
          </Box>
          {!isGroupFocused && <Box display="flex">Peepee Poopoo</Box>}
          <Icon>{isGroupFocused ? <DownIcon /> : <UpIcon />}</Icon>
        </Box>
        <Button
          variant="outlined"
          color="error"
          onClick={() => onRemoveGroup()}
        >
          Remove
        </Button>
      </Box>
      <Collapse
        in={isGroupFocused}
        timeout="auto"
        unmountOnExit
        sx={{ width: "100%" }}
      >
        <Stack direction="column" spacing={1} minWidth="100%" width="100%">
          <Box display="flex" gap={1} minWidth="100%" width="100%">
            <Controller
              name={`rma_data.${groupIndex}.customer_feedback`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  value={value}
                  onChange={(e, v) => {
                    if (!!v) {
                      setValue(
                        `rma_data.${groupIndex}.customer`,
                        custOptions.find(
                          (opt) => opt.value.id === v.value.cf_cust_id,
                        ) || null,
                      );
                    } else {
                      setValue(`rma_data.${groupIndex}.customer`, null);
                    }
                    onChange(v);
                  }}
                  options={custFeedbackOptions}
                  getOptionLabel={(opt) => opt?.label || ""}
                  groupBy={(opt) => opt.group || ""}
                  renderInput={(params) => (
                    <TextField
                      label="Linked Customer Feedback"
                      error={!!errors?.customer_feedback}
                      helperText={errors?.customer_feedback?.message}
                      {...params}
                    />
                  )}
                  fullWidth
                  {...restField}
                />
              )}
            />
            <Controller
              name={`rma_data.${groupIndex}.customer`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  value={value}
                  onChange={(e, v) => {
                    onChange(v);
                  }}
                  options={custOptions}
                  getOptionLabel={(opt) => opt?.label || ""}
                  groupBy={(opt) => opt.group || ""}
                  renderInput={(params) => (
                    <TextField
                      label="Linked Customer"
                      error={!!errors?.customer}
                      helperText={errors?.customer?.message}
                      {...params}
                    />
                  )}
                  disabled={!!watchedCFOption}
                  fullWidth
                  {...restField}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={1} minWidth="100%" width="100%">
            <Controller
              name={`rma_data.${groupIndex}.contact.name`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <TextField
                  label="RMA Contact Name"
                  value={value}
                  onChange={onChange}
                  {...restField}
                  fullWidth
                  error={!!errors?.contact?.name}
                  helperText={errors?.contact?.name?.message}
                />
              )}
            />
            <Controller
              name={`rma_data.${groupIndex}.contact.email`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <TextField
                  label="RMA Contact Email"
                  value={value}
                  onChange={onChange}
                  {...restField}
                  fullWidth
                  error={!!errors?.contact?.email}
                  helperText={errors?.contact?.email?.message}
                />
              )}
            />
            <Controller
              name={`rma_data.${groupIndex}.contact.phone`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <TextField
                  label="RMA Contact Phone"
                  value={value}
                  onChange={onChange}
                  {...restField}
                  fullWidth
                  error={!!errors?.contact?.phone}
                  helperText={errors?.contact?.phone?.message}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={1} minWidth="100%" width="100%">
            <Stack direction="column" spacing={0.5} width="50%">
              <AddressField
                control={control}
                fieldName={`rma_data.${groupIndex}.address`}
                fieldLabel="RMA Address"
                watchedAddr={watchedInitAddrObject}
                entityId={watchedCustOption?.value.id || null}
                preferredAddressId={null}
                staticOptions={[
                  {
                    label: "Custom",
                    type: "address",
                    value: {
                      id: "custom",
                      addressee: null,
                      attn: null,
                      line_1: "",
                      line_2: null,
                      line_3: null,
                      country: {
                        type: "country",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      state: {
                        type: "state",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      city: {
                        type: "city",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      zip: "",
                      phone: null,
                      email: null,
                    },
                  },
                ]}
                onPrefill={(addr) => {
                  console.log("On Prefill Addr Object", addr);
                  setValue(`rma_data.${groupIndex}.address`, addr, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onWorldLocSelect={(type, value) => {
                  console.log(
                    "On World Loc Select Type and Value",
                    type,
                    value,
                  );
                  setValue(`rma_data.${groupIndex}.address.${type}`, value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                disabled={false}
                ready={true}
                errors={{
                  main: errors?.address
                    ? "Contains errors to be addressed"
                    : null,
                  addressee: errors?.address?.addressee?.message || null,
                  attn: errors?.address?.attn?.message || null,
                  line_1: errors?.address?.line_1?.message || null,
                  line_2: errors?.address?.line_2?.message || null,
                  line_3: errors?.address?.line_3?.message || null,
                  city: errors?.address?.city?.message || null,
                  state: errors?.address?.state?.message || null,
                  country: errors?.address?.country?.message || null,
                  zip: errors?.address?.zip?.message || null,
                  phone: errors?.address?.phone?.message || null,
                  email: errors?.address?.email?.message || null,
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={useAltReturn}
                    onChange={(e, c) => {
                      setUseAltReturn(c);
                      setValue(
                        `rma_data.${groupIndex}.return_addr`,
                        c ? BlankOrderAddressObject : null,
                      );
                    }}
                  />
                }
                label="Use Alternate Return Address?"
                labelPlacement="end"
              />
            </Stack>
            <Box display="flex" width="50%">
              {useAltReturn ? (
                <AddressField
                  control={control}
                  fieldName={`rma_data.${groupIndex}.return_addr`}
                  fieldLabel="RMA Return Address"
                  watchedAddr={
                    watchedReturnAddrObject || BlankOrderAddressObject
                  }
                  entityId={watchedCustOption?.value.id || null}
                  preferredAddressId={null}
                  staticOptions={[
                    {
                      label: "Custom",
                      type: "address",
                      value: {
                        id: "custom",
                        addressee: null,
                        attn: null,
                        line_1: "",
                        line_2: null,
                        line_3: null,
                        country: {
                          type: "country",
                          parent: -1,
                          name: "",
                          code: "",
                          id: -1,
                        },
                        state: {
                          type: "state",
                          parent: -1,
                          name: "",
                          code: "",
                          id: -1,
                        },
                        city: {
                          type: "city",
                          parent: -1,
                          name: "",
                          code: "",
                          id: -1,
                        },
                        zip: "",
                        phone: null,
                        email: null,
                      },
                    },
                  ]}
                  onPrefill={(addr) => {
                    console.log("On Prefill Addr Object", addr);
                    setValue(`rma_data.${groupIndex}.return_addr`, addr, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                  onWorldLocSelect={(type, value) => {
                    console.log(
                      "On World Loc Select Type and Value",
                      type,
                      value,
                    );
                    setValue(
                      `rma_data.${groupIndex}.return_addr.${type}`,
                      value,
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                      },
                    );
                  }}
                  disabled={false}
                  ready={true}
                  errors={{
                    main: errors?.return_addr
                      ? "Contains errors to be addressed"
                      : null,
                    addressee: errors?.return_addr?.addressee?.message || null,
                    attn: errors?.return_addr?.attn?.message || null,
                    line_1: errors?.return_addr?.line_1?.message || null,
                    line_2: errors?.return_addr?.line_2?.message || null,
                    line_3: errors?.return_addr?.line_3?.message || null,
                    city: errors?.return_addr?.city?.message || null,
                    state: errors?.return_addr?.state?.message || null,
                    country: errors?.return_addr?.country?.message || null,
                    zip: errors?.return_addr?.zip?.message || null,
                    phone: errors?.return_addr?.phone?.message || null,
                    email: errors?.return_addr?.email?.message || null,
                  }}
                />
              ) : (
                <Typography variant="body1" textAlign={"center"}>
                  Using Initial RMA Address for Product Returns/Replacements
                  after RMA Processing
                </Typography>
              )}
            </Box>
          </Box>
          <Typography variant="h4">RMAs</Typography>
          {!!errors?.entries?.root?.message && (
            <Alert severity="error">{errors.entries.root.message}</Alert>
          )}
          <Box display="flex">
            <Button
              variant="outlined"
              color="info"
              onClick={() => {
                const len = fields.length;
                append(makeEmptyBatchRow());
                setActiveRMAIndex(len);
                setGroupConflicts((prev) => {
                  return prev.map((group, index) =>
                    index === groupIndex
                      ? [
                          ...group,
                          {
                            lot: null,
                            conflicts: [],
                          },
                        ]
                      : group,
                  );
                });
              }}
            >
              Add RMA to Group
            </Button>
          </Box>
          {fields.map((rma, rmaIndex) => {
            return (
              <RMABatchCreateRow
                errors={
                  errors?.entries?.[rmaIndex] as unknown as
                    | FieldErrors<RMAFormDataBatchCreateEntry>
                    | undefined
                }
                watch={watch}
                control={control}
                setValue={setValue}
                rowData={rma}
                groupIndex={groupIndex}
                rmaIndex={rmaIndex}
                activeIndex={activeRMAIndex}
                setActiveIndex={setActiveRMAIndex}
                itemOptions={itemOptions}
                lotOptions={lotOptions}
                reasonOptions={reasonOptions}
                onLotSelected={onLotSelected}
                lotConflicts={
                  groupConflicts[rmaIndex]
                    ? groupConflicts[rmaIndex].conflicts
                    : []
                }
                onRemove={() => {
                  console.log("RMA ROW ONREMOVE TRIGGERED", {
                    group: groupIndex,
                    rma: rmaIndex,
                  });
                  remove(rmaIndex);
                  onRemoveRMA(rmaIndex);
                }}
              />
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
};

function makeEmptyBatchGroup(): RMAFormDataBatchCreateGroup {
  return {
    customer_feedback: null,
    create_cf: false,
    customer: null,
    date_opened: null,
    contact: { name: "", email: null, phone: null },
    address: {
      attn: null,
      addressee: null,
      line_1: "",
      line_2: null,
      line_3: null,
      city: "",
      state: "",
      country: "",
      zip: "",
      phone: null,
      email: null,
    },
    return_addr: null,
    entries: [],
  };
}

const RMABatchCreateForm: React.FC<RMAModeFormProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeGroupIndex, setActiveGroupIndex] = useState<number>(-1);
  const [groupConflicts, setGroupConflicts] = useState<
    { lot: string | null; conflicts: string[] }[][]
  >([]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<RMAFormDataBatchCreateFull>({
    resolver: yupResolver(
      RMAValidationSchemaBatchCreateFull,
    ) as Resolver<RMAFormDataBatchCreateFull>,
    defaultValues: { rma_data: [] }, // no cast needed — DefaultValues<RMABatchCreate_v2> is partial
  });

  const {
    data: cfDataResponse,
    isPending: cfDataPending,
    isFetching: cfDataFetching,
    error: cfDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customer_feedback",
    alt_name: "cf",
    primary_fields: ["id", "cf_label", "cf_number", "gl_cf_num"],
    joins: [
      {
        table: "customers",
        alt_name: "cust",
        fields: ["id", "name"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "customer_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "name",
        asc: true,
      },
      {
        table: "customer_feedback",
        alt_name: "cf",
        field: "cf_number",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CF_OPTIONS: RMACFOption[] = useMemo(() => {
    if (cfDataResponse?.items) {
      return cfDataResponse.items.map((val) => ({
        label: val["cf.cf_label"],
        type: "customer_feedback",
        group: val["cust.name"],
        value: {
          id: val["cf.id"],
          nuc_cf_num: val["cf.cf_number"]
            ? `NUC-CF-${val["cf.cf_number"].toString().padStart(4, "0")}`
            : null,
          nuc_cf_label: val["cf.cf_label"],
          gl_cf: val["cf.gl_cf_num"] || null,
          cf_cust_id: val["cust.id"],
        },
      }));
    } else {
      return [];
    }
  }, [cfDataResponse]);

  const {
    data: custDataResponse,
    isPending: custDataPending,
    isFetching: custDataFetching,
    error: custDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customers",
    alt_name: "cust",
    primary_fields: ["id", "name", "code"],
    joins: [],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "code",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CUST_OPTIONS: RMACustOption[] = useMemo(() => {
    if (custDataResponse?.items) {
      return custDataResponse.items.map((val) => ({
        label: `${val["cust.code"]} - ${val["cust.name"]}`,
        type: "customer",
        value: {
          id: val["cust.id"],
          code: val["cust.code"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [custDataResponse]);

  const {
    data: itemDataResponse,
    isPending: itemDataPending,
    isFetching: itemDataFetching,
    error: itemDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "items",
    alt_name: "item",
    primary_fields: ["id", "name", "number", "catalog_name"],
    joins: [],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const ITEM_OPTIONS: RMAItemOption[] = useMemo(() => {
    if (itemDataResponse?.items) {
      return itemDataResponse.items.map((val) => ({
        label: `${val["item.catalog_name"]} - ${val["item.name"]}`,
        type: "item",
        value: {
          id: val["item.id"],
          catalog: val["item.catalog_name"],
          number: val["item.number"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [itemDataResponse]);

  const {
    data: lotDataResponse,
    isPending: lotDataPending,
    isFetching: lotDataFetching,
    error: lotDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "inventory_lots",
    alt_name: "lot",
    primary_fields: ["id", "lot_number"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "catalog_name"],
        condition: {
          remote_table: "inventory_lots",
          alt_name: "lot",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const LOT_OPTIONS: RMALotOption[] = useMemo(() => {
    if (lotDataResponse?.items) {
      return lotDataResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        group: val["item.catalog_name"],
        value: {
          id: val["lot.id"],
          item_id: val["item.id"],
        },
      }));
    } else {
      return [];
    }
  }, [lotDataResponse]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "rma_data",
  });

  React.useEffect(() => {
    if (open) {
      append(makeEmptyBatchGroup());
      setActiveGroupIndex(0);
      setGroupConflicts((prev) => {
        return [...prev, []];
      });
    }
  }, [append, open]);

  function handleLotOptionSelected(
    lotOpt: RMALotOption | null,
    rmaIndex: number,
    groupIndex: number,
  ): void {
    setGroupConflicts((prev) => {
      const newLotLabel = lotOpt?.label ?? null;

      // First, build updated lot labels
      const updatedLots = prev.map((group, groupInd) =>
        groupInd === groupIndex
          ? group.map((rma, rmaInd) =>
              rmaInd === rmaIndex ? { ...rma, lot: newLotLabel } : rma,
            )
          : group,
      );

      // Then recompute ALL conflicts from scratch based on current lot selections
      return updatedLots.map((thisGroup, thisGroupInd) => {
        return thisGroup.map((thisRMA, thisRMAInd) => {
          const conflicts: string[] = [];
          updatedLots.forEach((otherGroup, otherGroupInd) => {
            otherGroup.forEach((otherRMA, otherRMAInd) => {
              if (
                otherGroupInd === thisGroupInd &&
                otherRMAInd === thisRMAInd
              ) {
                return true;
              }
              if (thisRMA.lot !== null && thisRMA.lot === otherRMA.lot) {
                conflicts.push(`${otherGroupInd}.${otherRMAInd}`);
              }
            });
          });
          return { ...thisRMA, conflicts };
        });
      });
    });
  }

  const onSubmit = async (data: RMAFormDataBatchCreateFull) => {
    console.log("SUBMIT SUCCESS");
    try {
      setError(null);
      setSuccess(null);
      console.log(data);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to create RMAs");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setError(null);
      setSuccess(null);
      setActiveGroupIndex(-1);
      setGroupConflicts([]);
      replace([]);
      onClose();
    }
  };

  return (
    <RMADialogShell
      open={open}
      mode="create"
      isSubmitting={isSubmitting}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      debugValues={getValues()}
      debugErrors={{ formErrors: errors, lotConflicts: groupConflicts }}
      actions={
        <>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit, console.error)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create RMAs"}
          </Button>
        </>
      }
      user={user}
    >
      <Stack spacing={3}>
        {!!errors.rma_data?.root?.message && (
          <Alert severity="error">{errors.rma_data.root.message}</Alert>
        )}
        <Box display="flex">
          <Button
            variant="outlined"
            color="info"
            onClick={() => {
              const len = fields.length;
              append(makeEmptyBatchGroup());
              setActiveGroupIndex(len);
              setGroupConflicts((prev) => {
                return [...prev, []];
              });
            }}
          >
            Add RMA Group
          </Button>
        </Box>
        <Stack
          direction="column"
          spacing={3}
          width="100%"
          maxHeight="80%"
          overflow="auto"
        >
          <List>
            {fields.map((group, index) => {
              return (
                <RMABatchCreateGroup
                  errors={
                    (errors.rma_data?.[
                      index
                    ] as unknown as FieldErrors<RMAFormDataBatchCreateGroup>) ||
                    undefined
                  }
                  watch={watch}
                  control={control}
                  setValue={setValue}
                  groupData={group}
                  groupIndex={index}
                  activeGroupIndex={activeGroupIndex}
                  setActiveGroupIndex={setActiveGroupIndex}
                  custFeedbackOptions={CF_OPTIONS}
                  custOptions={CUST_OPTIONS}
                  itemOptions={ITEM_OPTIONS}
                  lotOptions={LOT_OPTIONS}
                  reasonOptions={RMA_REASON_OPTIONS}
                  groupConflicts={groupConflicts[index]}
                  setGroupConflicts={setGroupConflicts}
                  onLotSelected={handleLotOptionSelected}
                  onRemoveGroup={() => {
                    remove(index);
                    setGroupConflicts((prev) => {
                      const updated = prev.filter((_, i) => i !== index); // new array, no splice
                      // Recompute conflicts after removal
                      return updated.map((thisGroup, thisGroupInd) => {
                        return thisGroup.map((thisRMA, thisRMAInd) => {
                          const conflicts: string[] = [];
                          updated.forEach((otherGroup, otherGroupInd) => {
                            otherGroup.forEach((otherRMA, otherRMAInd) => {
                              if (
                                otherGroupInd === thisGroupInd &&
                                otherRMAInd === thisRMAInd
                              ) {
                                return true;
                              }
                              if (
                                thisRMA.lot !== null &&
                                thisRMA.lot === otherRMA.lot
                              ) {
                                conflicts.push(
                                  `${otherGroupInd}.${otherRMAInd}`,
                                );
                              }
                            });
                          });
                          return { ...thisRMA, conflicts };
                        });
                      });
                    });
                  }}
                  onRemoveRMA={(rmaIndex) => {
                    console.log("ON REMOVE RMA TRIGGER", { group: index });
                    setGroupConflicts((prev) => {
                      console.log("PREVIOUS LOT CONFLICTS", prev);
                      const updated = prev.map((thisGroup, thisGroupInd) =>
                        thisGroupInd === index
                          ? thisGroup.filter(
                              (conflict, cInd) => cInd !== rmaIndex,
                            )
                          : thisGroup,
                      );
                      // Recompute conflicts after removal
                      return updated.map((thisGroup, thisGroupInd) => {
                        return thisGroup.map((thisRMA, thisRMAInd) => {
                          const conflicts: string[] = [];
                          updated.forEach((otherGroup, otherGroupInd) => {
                            otherGroup.forEach((otherRMA, otherRMAInd) => {
                              if (
                                otherGroupInd === thisGroupInd &&
                                otherRMAInd === thisRMAInd
                              ) {
                                return true;
                              }
                              if (
                                thisRMA.lot !== null &&
                                thisRMA.lot === otherRMA.lot
                              ) {
                                conflicts.push(
                                  `${otherGroupInd}.${otherRMAInd}`,
                                );
                              }
                            });
                          });
                          return { ...thisRMA, conflicts };
                        });
                      });
                    });
                  }}
                />
              );
            })}
          </List>
        </Stack>
      </Stack>
    </RMADialogShell>
  );
};

const createFormDefaults: DefaultValues<RMAFormDataCreate> = {
  customer_feedback: null,
  customer: null,
  item: null,
  lot: null,
  reason: null,
  reason_other: null,
  rma_details: "",
  quantity: 0,
  date_opened: new Date(),
  contact: {
    name: "",
    email: null,
    phone: null,
  },
  address: {
    attn: null,
    addressee: null,
    line_1: "",
    line_2: null,
    line_3: null,
    city: "",
    state: "",
    country: "",
    zip: "",
    phone: null,
    email: null,
  },
  return_addr: null,
};

const RMACreateForm: React.FC<RMAModeFormProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [useAltReturn, setUseAltReturn] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<RMAFormDataCreate>({
    resolver: yupResolver(
      RMAValidationSchemaCreate,
    ) as Resolver<RMAFormDataCreate>,
    defaultValues: createFormDefaults,
  });

  const {
    data: cfDataResponse,
    isPending: cfDataPending,
    isFetching: cfDataFetching,
    error: cfDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customer_feedback",
    alt_name: "cf",
    primary_fields: ["id", "cf_label", "cf_number", "gl_cf_num"],
    joins: [
      {
        table: "customers",
        alt_name: "cust",
        fields: ["id", "name"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "customer_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "name",
        asc: true,
      },
      {
        table: "customer_feedback",
        alt_name: "cf",
        field: "cf_number",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CF_OPTIONS: RMACFOption[] = useMemo(() => {
    if (cfDataResponse?.items) {
      return cfDataResponse.items.map((val) => ({
        label: val["cf.cf_label"],
        type: "customer_feedback",
        group: val["cust.name"],
        value: {
          id: val["cf.id"],
          nuc_cf_num: val["cf.cf_number"]
            ? `NUC-CF-${val["cf.cf_number"].toString().padStart(4, "0")}`
            : null,
          nuc_cf_label: val["cf.cf_label"],
          gl_cf: val["cf.gl_cf_num"] || null,
          cf_cust_id: val["cust.id"],
        },
      }));
    } else {
      return [];
    }
  }, [cfDataResponse]);

  const {
    data: custDataResponse,
    isPending: custDataPending,
    isFetching: custDataFetching,
    error: custDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customers",
    alt_name: "cust",
    primary_fields: ["id", "name", "code"],
    joins: [],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "code",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CUST_OPTIONS: RMACustOption[] = useMemo(() => {
    if (custDataResponse?.items) {
      return custDataResponse.items.map((val) => ({
        label: `${val["cust.code"]} - ${val["cust.name"]}`,
        type: "customer",
        value: {
          id: val["cust.id"],
          code: val["cust.code"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [custDataResponse]);

  const {
    data: itemDataResponse,
    isPending: itemDataPending,
    isFetching: itemDataFetching,
    error: itemDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "items",
    alt_name: "item",
    primary_fields: ["id", "name", "number", "catalog_name"],
    joins: [],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const ITEM_OPTIONS: RMAItemOption[] = useMemo(() => {
    if (itemDataResponse?.items) {
      return itemDataResponse.items.map((val) => ({
        label: `${val["item.catalog_name"]} - ${val["item.name"]}`,
        type: "item",
        value: {
          id: val["item.id"],
          catalog: val["item.catalog_name"],
          number: val["item.number"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [itemDataResponse]);

  const {
    data: lotDataResponse,
    isPending: lotDataPending,
    isFetching: lotDataFetching,
    error: lotDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "inventory_lots",
    alt_name: "lot",
    primary_fields: ["id", "lot_number"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "catalog_name"],
        condition: {
          remote_table: "inventory_lots",
          alt_name: "lot",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const LOT_OPTIONS: RMALotOption[] = useMemo(() => {
    if (lotDataResponse?.items) {
      return lotDataResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        group: val["item.catalog_name"],
        value: {
          id: val["lot.id"],
          item_id: val["item.id"],
        },
      }));
    } else {
      return [];
    }
  }, [lotDataResponse]);

  React.useEffect(() => {
    if (open) {
      reset(createFormDefaults);
    }
  }, [reset, open]);

  const onSubmit = async (data: RMAFormDataCreate) => {
    console.log("SUBMIT SUCCESS");
    try {
      setError(null);
      setSuccess(null);
      console.log(data);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to create RMAs");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset(createFormDefaults);
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const [
    watchedCFOption,
    watchedCustOption,
    watchedInitAddrObject,
    watchedReturnAddrObject,
    watchedReasonOption,
  ] = watch([
    "customer_feedback",
    "customer",
    "address",
    "return_addr",
    "reason",
  ]);

  return (
    <RMADialogShell
      open={open}
      mode="create"
      isSubmitting={isSubmitting}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      debugValues={getValues()}
      debugErrors={{ formErrors: errors }}
      actions={
        <>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit, console.error)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create RMA"}
          </Button>
        </>
      }
      user={user}
    >
      <Stack direction="column" spacing={1} minWidth="100%" width="100%">
        <Box display="flex" gap={1} minWidth="100%" width="100%">
          <Controller
            name={`customer_feedback`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  if (!!v) {
                    setValue(
                      `customer`,
                      CUST_OPTIONS.find(
                        (opt) => opt.value.id === v.value.cf_cust_id,
                      ) || null,
                    );
                  } else {
                    setValue(`customer`, null);
                  }
                  onChange(v);
                }}
                options={CF_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                groupBy={(opt) => opt.group || ""}
                renderInput={(params) => (
                  <TextField
                    label="Linked Customer Feedback"
                    error={!!errors?.customer_feedback}
                    helperText={errors?.customer_feedback?.message}
                    {...params}
                  />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`customer`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={CUST_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                groupBy={(opt) => opt.group || ""}
                renderInput={(params) => (
                  <TextField
                    label="Linked Customer"
                    error={!!errors?.customer}
                    helperText={errors?.customer?.message}
                    {...params}
                  />
                )}
                disabled={!!watchedCFOption}
                fullWidth
                {...restField}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1} minWidth="100%" width="100%">
          <Controller
            name={`contact.name`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Name"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
                error={!!errors?.contact?.name}
                helperText={errors?.contact?.name?.message}
              />
            )}
          />
          <Controller
            name={`contact.email`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Email"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
                error={!!errors?.contact?.email}
                helperText={errors?.contact?.email?.message}
              />
            )}
          />
          <Controller
            name={`contact.phone`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Phone"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
                error={!!errors?.contact?.phone}
                helperText={errors?.contact?.phone?.message}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1} minWidth="100%" width="100%">
          <Stack direction="column" spacing={0.5} width="50%">
            <AddressField
              control={control}
              fieldName={`address`}
              fieldLabel="RMA Address"
              watchedAddr={watchedInitAddrObject}
              entityId={watchedCustOption?.value.id || null}
              preferredAddressId={null}
              staticOptions={[
                {
                  label: "Custom",
                  type: "address",
                  value: {
                    id: "custom",
                    addressee: null,
                    attn: null,
                    line_1: "",
                    line_2: null,
                    line_3: null,
                    country: {
                      type: "country",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    state: {
                      type: "state",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    city: {
                      type: "city",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    zip: "",
                    phone: null,
                    email: null,
                  },
                },
              ]}
              onPrefill={(addr) => {
                console.log("On Prefill Addr Object", addr);
                setValue(`address`, addr, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              onWorldLocSelect={(type, value) => {
                console.log("On World Loc Select Type and Value", type, value);
                setValue(`address.${type}`, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              disabled={false}
              ready={true}
              errors={{
                main: errors?.address
                  ? "Contains errors to be addressed"
                  : null,
                addressee: errors?.address?.addressee?.message || null,
                attn: errors?.address?.attn?.message || null,
                line_1: errors?.address?.line_1?.message || null,
                line_2: errors?.address?.line_2?.message || null,
                line_3: errors?.address?.line_3?.message || null,
                city: errors?.address?.city?.message || null,
                state: errors?.address?.state?.message || null,
                country: errors?.address?.country?.message || null,
                zip: errors?.address?.zip?.message || null,
                phone: errors?.address?.phone?.message || null,
                email: errors?.address?.email?.message || null,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={useAltReturn}
                  onChange={(e, c) => {
                    setUseAltReturn(c);
                    setValue(`return_addr`, c ? BlankOrderAddressObject : null);
                  }}
                />
              }
              label="Use Alternate Return Address?"
              labelPlacement="end"
            />
          </Stack>
          <Box display="flex" width="50%">
            {useAltReturn ? (
              <AddressField
                control={control}
                fieldName={`return_addr`}
                fieldLabel="RMA Return Address"
                watchedAddr={watchedReturnAddrObject || BlankOrderAddressObject}
                entityId={watchedCustOption?.value.id || null}
                preferredAddressId={null}
                staticOptions={[
                  {
                    label: "Custom",
                    type: "address",
                    value: {
                      id: "custom",
                      addressee: null,
                      attn: null,
                      line_1: "",
                      line_2: null,
                      line_3: null,
                      country: {
                        type: "country",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      state: {
                        type: "state",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      city: {
                        type: "city",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      zip: "",
                      phone: null,
                      email: null,
                    },
                  },
                ]}
                onPrefill={(addr) => {
                  console.log("On Prefill Addr Object", addr);
                  setValue(`return_addr`, addr, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onWorldLocSelect={(type, value) => {
                  console.log(
                    "On World Loc Select Type and Value",
                    type,
                    value,
                  );
                  setValue(`return_addr.${type}`, value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                disabled={false}
                ready={true}
                errors={{
                  main: errors?.return_addr
                    ? "Contains errors to be addressed"
                    : null,
                  addressee: errors?.return_addr?.addressee?.message || null,
                  attn: errors?.return_addr?.attn?.message || null,
                  line_1: errors?.return_addr?.line_1?.message || null,
                  line_2: errors?.return_addr?.line_2?.message || null,
                  line_3: errors?.return_addr?.line_3?.message || null,
                  city: errors?.return_addr?.city?.message || null,
                  state: errors?.return_addr?.state?.message || null,
                  country: errors?.return_addr?.country?.message || null,
                  zip: errors?.return_addr?.zip?.message || null,
                  phone: errors?.return_addr?.phone?.message || null,
                  email: errors?.return_addr?.email?.message || null,
                }}
              />
            ) : (
              <Typography variant="body1" textAlign={"center"}>
                Using Initial RMA Address for Product Returns/Replacements after
                RMA Processing
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Controller
            name={`reason`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                  setValue(`reason_other`, null);
                }}
                options={RMA_REASON_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField
                    label="Reason for Return"
                    {...params}
                    error={!!errors?.reason}
                    helperText={errors?.reason?.message}
                  />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`reason_other`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="Reason - Other Context"
                value={value || ""}
                onChange={onChange}
                error={!!errors?.reason_other}
                helperText={errors?.reason_other?.message}
                {...restField}
                fullWidth
                disabled={watchedReasonOption?.label !== "Other"}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1}>
          <Controller
            name={`item`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={ITEM_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField
                    label="RMA Item"
                    {...params}
                    error={!!errors?.item}
                    helperText={errors?.item?.message}
                  />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`lot`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={LOT_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => {
                  return (
                    <TextField
                      label="RMA Lot"
                      {...params}
                      error={!!errors?.lot}
                      helperText={errors?.lot?.message}
                    />
                  );
                }}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`quantity`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <NumericEntryField
                value={value}
                onChange={onChange}
                label="Quantity Returning"
                decimalPlaces={0}
                isCurrency={false}
                error={!!errors?.quantity}
                helperText={errors?.quantity?.message}
                textFieldProps={{ fullWidth: true }}
              />
            )}
          />
        </Box>
        <Controller
          name={`rma_details`} // fully typed path
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="RMA Details"
              error={!!errors?.rma_details}
              helperText={errors?.rma_details?.message}
              fullWidth
            />
          )}
        />
      </Stack>
    </RMADialogShell>
  );
};

const RMAEditForm: React.FC<RMAModeFormProps> = ({
  open,
  rmaData,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [useAltReturn, setUseAltReturn] = useState<boolean>(false);

  const {
    watch,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
  } = useForm<RMAFormDataUpdate>({
    resolver: yupResolver(
      RMAValidationSchemaUpdate,
    ) as Resolver<RMAFormDataUpdate>,
    defaultValues: {
      customer_feedback: null,
      customer: {
        label: "SELECT CUSTOMER",
        type: "customer",
        value: {
          id: "invalidcust",
          code: "N/A",
          name: "N/A",
        },
      },
      item: {
        label: "SELECT ITEM",
        type: "item",
        value: {
          id: "invaliditem",
          catalog: "N/A",
          number: "N/A",
          name: "N/A",
        },
      },
      lot: null,
      reason: {
        label: "SERVICE",
        type: "rma_reason",
        value: {
          id: "SERVICE",
        },
      },
      reason_other: null,
      rma_details: "",
      quantity: 0,
      date_opened: new Date(),
      date_inspected: null,
      date_estimated_completion: null,
      date_closed: null,
      contact: {
        name: "",
        email: null,
        phone: null,
      },
      address: {
        attn: null,
        addressee: null,
        line_1: "",
        line_2: null,
        line_3: null,
        city: "",
        state: "",
        country: "",
        zip: "",
        phone: null,
        email: null,
      },
      return_addr: null,
    },
  });

  React.useEffect(() => {
    if (open) {
      if (!rmaData) {
        setError(
          "RMA Data is Required when viewing an RMA Record. Refresh the page and try again. Notify IT if this issue persists.",
        );
        return;
      } else {
        reset({
          customer_feedback: rmaData.cf_id
            ? {
                label: rmaData.nuc_cf_label!,
                type: "customer_feedback",
                group: rmaData.cust_name,
                value: {
                  id: rmaData.cf_id,
                  nuc_cf_num: rmaData.nuc_cf_num!,
                  nuc_cf_label: rmaData.nuc_cf_label!,
                  gl_cf: rmaData.gl_cf,
                  cf_cust_id: rmaData.cf_cust_id!,
                },
              }
            : null,
          customer: {
            label: `${rmaData.cust_code} - ${rmaData.cust_name}`,
            type: "customer",
            value: {
              id: rmaData.cust_id,
              name: rmaData.cust_name,
              code: rmaData.cust_code,
            },
          },
          item: {
            label: `${rmaData.item_catalog} - ${rmaData.item_name}`,
            type: "item",
            value: {
              id: rmaData.item_id,
              catalog: rmaData.item_catalog,
              number: rmaData.item_num,
              name: rmaData.item_name,
            },
          },
          lot: rmaData.lot_id
            ? {
                label: rmaData.lot_num!,
                type: "lot",
                value: {
                  id: rmaData.lot_id,
                  item_id: rmaData.lot_item_id!,
                },
              }
            : null,
          reason: {
            label: stringUtils.capFirstLetter(
              rmaData.reason.replaceAll("_", " "),
            ),
            type: "rma_reason",
            value: {
              id: rmaData.reason,
            },
          },
          reason_other: rmaData.reason_other,
          rma_details: rmaData.rma_details,
          quantity: rmaData.quantity,
          date_opened: new Date(rmaData.date_opened),
          contact: {
            name: rmaData.contact.name,
            email: rmaData.contact.email,
            phone: rmaData.contact.phone,
          },
          address: {
            attn: rmaData.address.attn,
            addressee: rmaData.address.addressee,
            line_1: rmaData.address.line_1,
            line_2: rmaData.address.line_2,
            line_3: rmaData.address.line_3,
            city: rmaData.address.city,
            state: rmaData.address.state,
            country: rmaData.address.country,
            zip: rmaData.address.zip,
            phone: rmaData.address.phone,
            email: rmaData.address.email,
          },
          return_addr: rmaData.return_addr
            ? {
                attn: rmaData.return_addr.attn,
                addressee: rmaData.return_addr.addressee,
                line_1: rmaData.return_addr.line_1,
                line_2: rmaData.return_addr.line_2,
                line_3: rmaData.return_addr.line_3,
                city: rmaData.return_addr.city,
                state: rmaData.return_addr.state,
                country: rmaData.return_addr.country,
                zip: rmaData.return_addr.zip,
                phone: rmaData.return_addr.phone,
                email: rmaData.return_addr.email,
              }
            : null,
        });
        setUseAltReturn(!!rmaData.return_addr);
      }
    }
  }, [open, rmaData, reset]);

  const [
    watchedCFOption,
    watchedCustOption,
    watchedInitAddrObject,
    watchedReturnAddrObject,
    watchedReasonOption,
  ] = watch([
    `customer_feedback`,
    `customer`,
    `address`,
    `return_addr`,
    `reason`,
  ]);

  const {
    data: cfDataResponse,
    isPending: cfDataPending,
    isFetching: cfDataFetching,
    error: cfDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customer_feedback",
    alt_name: "cf",
    primary_fields: ["id", "cf_label", "cf_number", "gl_cf_num"],
    joins: [
      {
        table: "customers",
        alt_name: "cust",
        fields: ["id", "name"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "customer_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "name",
        asc: true,
      },
      {
        table: "customer_feedback",
        alt_name: "cf",
        field: "cf_number",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CF_OPTIONS: RMACFOption[] = useMemo(() => {
    if (cfDataResponse?.items) {
      return cfDataResponse.items.map((val) => ({
        label: val["cf.cf_label"],
        type: "customer_feedback",
        group: val["cust.name"],
        value: {
          id: val["cf.id"],
          nuc_cf_num: val["cf.cf_number"]
            ? `NUC-CF-${val["cf.cf_number"].toString().padStart(4, "0")}`
            : null,
          nuc_cf_label: val["cf.cf_label"],
          gl_cf: val["cf.gl_cf_num"] || null,
          cf_cust_id: val["cust.id"],
        },
      }));
    } else {
      return [];
    }
  }, [cfDataResponse]);

  const {
    data: custDataResponse,
    isPending: custDataPending,
    isFetching: custDataFetching,
    error: custDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "customers",
    alt_name: "cust",
    primary_fields: ["id", "name", "code"],
    joins: [],
    filters: [],
    sorts: [
      {
        table: "customers",
        alt_name: "cust",
        field: "code",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const CUST_OPTIONS: RMACustOption[] = useMemo(() => {
    if (custDataResponse?.items) {
      return custDataResponse.items.map((val) => ({
        label: `${val["cust.code"]} - ${val["cust.name"]}`,
        type: "customer",
        value: {
          id: val["cust.id"],
          code: val["cust.code"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [custDataResponse]);

  const {
    data: itemDataResponse,
    isPending: itemDataPending,
    isFetching: itemDataFetching,
    error: itemDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "items",
    alt_name: "item",
    primary_fields: ["id", "name", "number", "catalog_name"],
    joins: [],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const ITEM_OPTIONS: RMAItemOption[] = useMemo(() => {
    if (itemDataResponse?.items) {
      return itemDataResponse.items.map((val) => ({
        label: `${val["item.catalog_name"]} - ${val["item.name"]}`,
        type: "item",
        value: {
          id: val["item.id"],
          catalog: val["item.catalog_name"],
          number: val["item.number"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [itemDataResponse]);

  const {
    data: lotDataResponse,
    isPending: lotDataPending,
    isFetching: lotDataFetching,
    error: lotDataError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "inventory_lots",
    alt_name: "lot",
    primary_fields: ["id", "lot_number"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "catalog_name"],
        condition: {
          remote_table: "inventory_lots",
          alt_name: "lot",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          operator: "==",
          value: null,
          is_not: true,
          case_insensitive: false,
          inclusive_start: false,
          inclusive_end: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const LOT_OPTIONS: RMALotOption[] = useMemo(() => {
    if (lotDataResponse?.items) {
      return lotDataResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        group: val["item.catalog_name"],
        value: {
          id: val["lot.id"],
          item_id: val["item.id"],
        },
      }));
    } else {
      return [];
    }
  }, [lotDataResponse]);

  const onSubmit = async (data: RMAFormDataUpdate) => {
    try {
      setError(null);
      setSuccess(null);
      console.log(data);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to update RMA");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <RMADialogShell
      open={open}
      mode="edit"
      isSubmitting={isSubmitting}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      debugValues={getValues()}
      debugErrors={errors}
      actions={
        <>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit, console.error)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update RMA"}
          </Button>
        </>
      }
      user={user}
    >
      <Stack direction="column" spacing={1}>
        <Box display="flex" gap={1}>
          <Controller
            name={`customer_feedback`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  if (!!v) {
                    setValue(
                      `customer`,
                      CUST_OPTIONS.find(
                        (opt) => opt.value.id === v.value.cf_cust_id,
                      ) || null,
                    );
                  } else {
                    setValue(`customer`, null);
                  }
                  onChange(v);
                }}
                options={CF_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                groupBy={(opt) => opt.group || ""}
                renderInput={(params) => (
                  <TextField label="Linked Customer Feedback" {...params} />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`customer`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={CUST_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                groupBy={(opt) => opt.group || ""}
                renderInput={(params) => (
                  <TextField label="Linked Customer" {...params} />
                )}
                disabled={!!watchedCFOption}
                fullWidth
                {...restField}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1}>
          <Controller
            name={`reason`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                  setValue(`reason_other`, null);
                }}
                options={RMA_REASON_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField label="Reason for Return" {...params} />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`reason_other`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="Reason - Other"
                value={value || ""}
                onChange={onChange}
                {...restField}
                fullWidth
                disabled={watchedReasonOption?.label !== "Other"}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1}>
          <Controller
            name={`item`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={ITEM_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField label="RMA Item" {...params} />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`lot`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                }}
                options={LOT_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField label="RMA Lot" {...params} />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Controller
            name={`quantity`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <NumericEntryField
                value={value}
                onChange={onChange}
                label="Quantity Returning"
                decimalPlaces={0}
                isCurrency={false}
                textFieldProps={{ fullWidth: true }}
              />
            )}
          />
        </Box>
        <Controller
          name={`rma_details`} // fully typed path
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="RMA Details"
              error={!!errors.rma_details}
              helperText={errors.rma_details?.message}
              fullWidth
            />
          )}
        />
        <Box display="flex" gap={1}>
          <Controller
            name={`contact.name`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Name"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
              />
            )}
          />
          <Controller
            name={`contact.email`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Email"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
              />
            )}
          />
          <Controller
            name={`contact.phone`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <TextField
                label="RMA Contact Phone"
                value={value}
                onChange={onChange}
                {...restField}
                fullWidth
              />
            )}
          />
        </Box>
        <Box display="flex" gap={1}>
          <Stack direction="column" spacing={0.5} width="50%">
            <AddressField
              control={control}
              fieldName={`address`}
              fieldLabel="RMA Address"
              watchedAddr={watchedInitAddrObject}
              entityId={watchedCustOption?.value.id || null}
              preferredAddressId={null}
              staticOptions={[
                {
                  label: "Custom",
                  type: "address",
                  value: {
                    id: "custom",
                    addressee: null,
                    attn: null,
                    line_1: "",
                    line_2: null,
                    line_3: null,
                    country: {
                      type: "country",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    state: {
                      type: "state",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    city: {
                      type: "city",
                      parent: -1,
                      name: "",
                      code: "",
                      id: -1,
                    },
                    zip: "",
                    phone: null,
                    email: null,
                  },
                },
              ]}
              onPrefill={(addr) => {
                console.log("On Prefill Addr Object", addr);
                setValue(`address`, addr, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              onWorldLocSelect={(type, value) => {
                console.log("On World Loc Select Type and Value", type, value);
                setValue(`address.${type}`, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              disabled={false}
              ready={true}
              errors={{
                main: errors.address ? "Contains errors to be addressed" : null,
                addressee: errors.address?.addressee?.message || null,
                attn: errors.address?.attn?.message || null,
                line_1: errors.address?.line_1?.message || null,
                line_2: errors.address?.line_2?.message || null,
                line_3: errors.address?.line_3?.message || null,
                city: errors.address?.city?.message || null,
                state: errors.address?.state?.message || null,
                country: errors.address?.country?.message || null,
                zip: errors.address?.zip?.message || null,
                phone: errors.address?.phone?.message || null,
                email: errors.address?.email?.message || null,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={useAltReturn}
                  onChange={(e, c) => {
                    setUseAltReturn(c);
                    setValue(`return_addr`, c ? BlankOrderAddressObject : null);
                  }}
                />
              }
              label="Use Alternate Return Address?"
              labelPlacement="end"
            />
          </Stack>
          <Box display="flex" width="50%">
            {useAltReturn ? (
              <AddressField
                control={control}
                fieldName={`return_addr`}
                fieldLabel="RMA Return Address"
                watchedAddr={watchedReturnAddrObject || BlankOrderAddressObject}
                entityId={watchedCustOption?.value.id || null}
                preferredAddressId={null}
                staticOptions={[
                  {
                    label: "Custom",
                    type: "address",
                    value: {
                      id: "custom",
                      addressee: null,
                      attn: null,
                      line_1: "",
                      line_2: null,
                      line_3: null,
                      country: {
                        type: "country",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      state: {
                        type: "state",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      city: {
                        type: "city",
                        parent: -1,
                        name: "",
                        code: "",
                        id: -1,
                      },
                      zip: "",
                      phone: null,
                      email: null,
                    },
                  },
                ]}
                onPrefill={(addr) => {
                  console.log("On Prefill Addr Object", addr);
                  setValue(`return_addr`, addr, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onWorldLocSelect={(type, value) => {
                  console.log(
                    "On World Loc Select Type and Value",
                    type,
                    value,
                  );
                  setValue(`return_addr.${type}`, value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                disabled={false}
                ready={true}
                errors={{
                  main: errors.return_addr
                    ? "Contains errors to be addressed"
                    : null,
                  addressee: errors.return_addr?.addressee?.message || null,
                  attn: errors.return_addr?.attn?.message || null,
                  line_1: errors.return_addr?.line_1?.message || null,
                  line_2: errors.return_addr?.line_2?.message || null,
                  line_3: errors.return_addr?.line_3?.message || null,
                  city: errors.return_addr?.city?.message || null,
                  state: errors.return_addr?.state?.message || null,
                  country: errors.return_addr?.country?.message || null,
                  zip: errors.return_addr?.zip?.message || null,
                  phone: errors.return_addr?.phone?.message || null,
                  email: errors.return_addr?.email?.message || null,
                }}
              />
            ) : (
              <Typography variant="body1" textAlign={"center"}>
                Using Initial RMA Address for Product Returns/Replacements after
                RMA Processing
              </Typography>
            )}
          </Box>
        </Box>
      </Stack>
    </RMADialogShell>
  );
};

const RMAReceiveBatchRow: React.FC<{
  rmaIndex: number;
  rmaOptions: RMAOption[];
  itemOptions: RMAItemOption[];
  rowData: FieldArrayWithId<RMAFormDataReceiveBatch, "rma_data", "id">;
  control: Control<RMAFormDataReceiveBatch, any, RMAFormDataReceiveBatch>;
  watch: UseFormWatch<RMAFormDataReceiveBatch>;
  errors: FieldErrors<RMAFormDataReceive> | undefined;
  setValue: UseFormSetValue<RMAFormDataReceiveBatch>;
  onRmaSelect: () => void;
  onLotSelect: () => void;
  onRemoveRow: () => void;
}> = ({
  rmaIndex,
  rmaOptions,
  itemOptions,
  rowData,
  control,
  watch,
  errors,
  setValue,
  onRmaSelect,
  onLotSelect,
  onRemoveRow,
}) => {
  const [itemDisabled, setItemDisabled] = React.useState<boolean>(false);
  const [lotDisabled, setLotDisabled] = React.useState<boolean>(false);
  const [watchedRmaOpt, watchedItemOpt, watchedLotOpt] = watch([
    `rma_data.${rmaIndex}.rma`,
    `rma_data.${rmaIndex}.item`,
    `rma_data.${rmaIndex}.lot`,
  ]);
  const {
    data: lotResponse,
    isLoading: lotLoading,
    error: lotError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "inventory_lots",
      alt_name: "lot",
      primary_fields: ["id", "lot_number", "item_id"],
      joins: [],
      filters: [
        [
          {
            table: "inventory_lots",
            alt_name: "lot",
            field: "item_id",
            operator: "==",
            value: watchedItemOpt?.value.id || null,
            is_not: false,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
          },
        ],
      ],
      sorts: [
        {
          table: "inventory_lots",
          alt_name: "lot",
          field: "lot_number",
          asc: true,
        },
      ],
      subqueries: [],
    },
    !!watchedItemOpt,
  );
  const LOT_OPTIONS: RMALotOption[] = useMemo(() => {
    if (lotResponse?.items) {
      return lotResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        value: {
          id: val["lot.id"],
          item_id: val["lot.item_id"],
        },
      }));
    }
    return [];
  }, [lotResponse]);
  return (
    <TableRow>
      <TableCell>
        <Controller
          name={`rma_data.${rmaIndex}.rma`}
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <Autocomplete
              value={value}
              onChange={(e, v) => {
                onChange(v);
                if (!!v) {
                  setValue(`rma_data.${rmaIndex}.item`, v.value.item, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  setItemDisabled(true);
                  setValue(`rma_data.${rmaIndex}.lot`, v.value.lot, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  setLotDisabled(!!v.value.lot);
                  setValue(`rma_data.${rmaIndex}.quantity`, v.value.quantity, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                } else {
                  setValue(`rma_data.${rmaIndex}.item`, null, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  setItemDisabled(false);
                  setValue(`rma_data.${rmaIndex}.lot`, null, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  setLotDisabled(false);
                  setValue(`rma_data.${rmaIndex}.quantity`, 0, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }
              }}
              options={rmaOptions}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => (
                <TextField
                  label="RMA"
                  {...params}
                  error={!!errors?.rma}
                  helperText={errors?.rma?.message}
                />
              )}
              fullWidth
              {...restField}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Controller
          name={`rma_data.${rmaIndex}.item`}
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <Autocomplete
              value={value}
              onChange={(e, v) => {
                onChange(v);
                if (
                  watchedRmaOpt?.value.item.value.id !== v?.value.id ||
                  watchedLotOpt?.value.item_id !== v?.value.id
                ) {
                  setValue(`rma_data.${rmaIndex}.lot`, null, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  setLotDisabled(false);
                  setValue(`rma_data.${rmaIndex}.quantity`, 0, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }
              }}
              disabled={itemDisabled}
              options={itemOptions}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => {
                params.InputProps.endAdornment = itemDisabled ? (
                  <Box display="flex">
                    <Tooltip title="Change RMA Item">
                      <IconButton
                        onClick={() => {
                          setValue(`rma_data.${rmaIndex}.item`, null, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          setItemDisabled(false);
                          setValue(`rma_data.${rmaIndex}.lot`, null, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          setLotDisabled(false);
                          setValue(`rma_data.${rmaIndex}.quantity`, 0, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  params.InputProps.endAdornment
                );
                return (
                  <TextField
                    label="Item"
                    {...params}
                    error={!!errors?.item}
                    helperText={errors?.item?.message}
                  />
                );
              }}
              fullWidth
              {...restField}
            />
          )}
        />
        <Controller
          name={`rma_data.${rmaIndex}.lot`}
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <Autocomplete
              value={value}
              onChange={(e, v) => {
                onChange(v);
              }}
              loading={lotLoading}
              options={LOT_OPTIONS}
              disabled={lotDisabled}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => {
                params.InputProps.endAdornment = lotDisabled ? (
                  <Box display="flex">
                    <Tooltip title="Change RMA Lot">
                      <IconButton
                        onClick={() => {
                          setValue(`rma_data.${rmaIndex}.lot`, null, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          setLotDisabled(false);
                          setValue(`rma_data.${rmaIndex}.quantity`, 0, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  params.InputProps.endAdornment
                );
                return (
                  <TextField
                    label="Lot"
                    {...params}
                    error={!!errors?.lot}
                    helperText={errors?.lot?.message}
                  />
                );
              }}
              fullWidth
              {...restField}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Controller
          name={`rma_data.${rmaIndex}.date`}
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <DatePicker
              label="Date Received"
              value={value}
              onChange={onChange}
              {...restField}
              slotProps={{
                textField: {
                  error: !!errors?.date,
                  helperText: errors?.date?.message,
                  fullWidth: true,
                },
              }}
            />
          )}
        />
        <Controller
          name={`rma_data.${rmaIndex}.quantity`}
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <NumericEntryField
              value={value}
              onChange={(v) => onChange(v)}
              decimalPlaces={0}
              isCurrency={false}
              textFieldProps={{ fullWidth: true, ...restField }}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            onRemoveRow();
          }}
        ></Button>
      </TableCell>
    </TableRow>
  );
};

const RMAReceiveBatchForm: React.FC<RMAModeFormProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<RMAFormDataReceiveBatch>({
    resolver: yupResolver(
      RMAValidationSchemaReceiveFull,
    ) as Resolver<RMAFormDataReceiveBatch>,
    defaultValues: { rma_data: [] }, // no cast needed — DefaultValues<RMABatchCreate_v2> is partial
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "rma_data",
  });

  const {
    data: rmaResponse,
    isLoading: rmaLoading,
    error: rmaError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "returned_materials",
      alt_name: "rma",
      primary_fields: ["id", "date_opened", "number", "quantity"],
      joins: [
        {
          table: "items",
          alt_name: "item",
          fields: ["id", "catalog_name", "name", "number"],
          condition: {
            remote_table: "returned_materials",
            alt_name: "rma",
            remote_field: "item_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "inventory_lots",
          alt_name: "lot",
          fields: ["id", "lot_number", "item_id"],
          condition: {
            remote_table: "returned_materials",
            alt_name: "rma",
            remote_field: "lot_id",
            local_field: "id",
          },
          outer: true,
        },
      ],
      filters: [
        [
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "status",
            operator: "==",
            value: ["AWAITING_RECEIPT"],
            is_not: false,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
          },
        ],
      ],
      sorts: [
        {
          table: "returned_materials",
          alt_name: "rma",
          field: "date_opened",
          asc: true,
        },
      ],
      subqueries: [],
    },
    open,
  );
  const RMA_OPTIONS: RMAOption[] = useMemo(() => {
    if (rmaResponse?.items) {
      return rmaResponse.items.map((val) => ({
        label: `${new Date(val["rma.date_opened"]).getFullYear().toString().slice(2)}-${val["rma.number"].toString().padStart(4, "0")}`,
        type: "rma_record",
        value: {
          id: val["rma.id"],
          item: {
            label: `${val["item.catalog_name"]} - ${val["item.name"]}`,
            type: "item",
            value: {
              id: val["item.id"],
              number: val["item.number"],
              name: val["item.name"],
              catalog: val["item.catalog_name"],
            },
          },
          lot: !!val["lot.id"]
            ? {
                label: val["lot.lot_number"],
                type: "lot",
                value: {
                  id: val["lot.id"],
                  item_id: val["lot.item_id"],
                },
              }
            : null,
          quantity: val["rma.quantity"],
        },
      }));
    }
    return [];
  }, [rmaResponse]);

  const {
    data: itemResponse,
    isLoading: itemLoading,
    error: itemError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "items",
      alt_name: "item",
      primary_fields: ["id", "catalog_name", "number", "name"],
      joins: [],
      filters: [
        [
          {
            table: "items",
            alt_name: "item",
            field: "catalog_name",
            operator: "==",
            value: null,
            is_not: true,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
          },
        ],
      ],
      sorts: [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          asc: true,
        },
      ],
      subqueries: [],
    },
    open,
  );
  const ITEM_OPTIONS: RMAItemOption[] = useMemo(() => {
    if (itemResponse?.items) {
      return itemResponse.items.map((val) => ({
        label: `${val["item.catalog_name"]}-${val["item.name"]}`,
        type: "item",
        value: {
          id: val["item.id"],
          number: val["item.number"],
          name: val["item.name"],
          catalog: val["item.catalog_name"],
        },
      }));
    }
    return [];
  }, [itemResponse]);

  const onSubmit = async (data: RMAFormDataReceiveBatch) => {
    try {
      setError(null);
      setSuccess(null);
      console.log(data);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to receive RMAs");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  function handleAddNewRow() {
    append({
      rma: null,
      item: null,
      lot: null,
      quantity: 0,
      date: new Date(),
    });
  }

  return (
    <RMADialogShell
      open={open}
      mode="receivebatch"
      isSubmitting={isSubmitting}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      debugValues={getValues()}
      debugErrors={errors}
      actions={
        <>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit, console.error)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Receiving..." : "Receive RMAs"}
          </Button>
        </>
      }
      user={user}
    >
      <Stack spacing={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Typography variant="h3">RMAs to Receive</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="30%">RMA</TableCell>
                  <TableCell width="30%">Item/Lot</TableCell>
                  <TableCell width="30%">Quantity/Date</TableCell>
                  <TableCell width="10%"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align="left">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleAddNewRow}
                    >
                      Add New Row
                    </Button>
                  </TableCell>
                </TableRow>
                {fields.map((rma, rmaIndex) => (
                  <RMAReceiveBatchRow
                    rmaIndex={rmaIndex}
                    rmaOptions={RMA_OPTIONS}
                    itemOptions={ITEM_OPTIONS}
                    rowData={rma}
                    control={control}
                    watch={watch}
                    errors={
                      (errors.rma_data?.[
                        rmaIndex
                      ] as unknown as FieldErrors<RMAFormDataReceive>) ||
                      undefined
                    }
                    setValue={setValue}
                    onRmaSelect={() => {}}
                    onLotSelect={() => {}}
                    onRemoveRow={() => {
                      remove(rmaIndex);
                    }}
                  />
                ))}
                {fields.length > 5 && (
                  <TableRow>
                    <TableCell colSpan={4} align="left">
                      <Button
                        variant="contained"
                        color="info"
                        onClick={handleAddNewRow}
                      >
                        Add New Row
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </LocalizationProvider>
      </Stack>
    </RMADialogShell>
  );
};

const RMAReceiveForm: React.FC<RMAModeFormProps> = ({
  open,
  rmaData,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [itemDisabled, setItemDisabled] = useState<boolean>(true);
  const [lotDisabled, setLotDisabled] = useState<boolean>(true);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<RMAFormDataReceive>({
    resolver: yupResolver(
      RMAValidationSchemaReceive,
    ) as Resolver<RMAFormDataReceive>,
    defaultValues: {
      rma: null,
      item: null,
      lot: null,
      quantity: 0,
      date: new Date(),
    }, // no cast needed — DefaultValues<RMABatchCreate_v2> is partial
  });

  const [watchedRmaOpt, watchedItemOpt, watchedLotOpt] = watch([
    "rma",
    "item",
    "lot",
  ]);

  React.useEffect(() => {
    if (open) {
      if (!!rmaData) {
        const lotOpt: RMALotOption | null = !!rmaData.lot_id
          ? {
              label: rmaData.lot_num || "UNKNOWN",
              type: "lot",
              value: {
                id: rmaData.lot_id,
                item_id: rmaData.lot_item_id || "UNKNOWN",
              },
            }
          : null;
        const rmaOpt: RMAOption = {
          label: `${rmaData.date_opened.getFullYear().toString().slice(2)}-${rmaData.number.toString().padStart(4, "0")}`,
          type: "rma_record",
          value: {
            id: rmaData.id,
            item: {
              label: `${rmaData.item_catalog} - ${rmaData.item_name}`,
              type: "item",
              value: {
                id: rmaData.item_id,
                number: rmaData.item_num,
                name: rmaData.item_name,
                catalog: rmaData.item_catalog,
              },
            },
            lot: lotOpt,
            quantity: rmaData.quantity,
          },
        };
        reset({
          rma: rmaOpt,
          item: rmaOpt.value.item,
          lot: lotOpt,
          quantity: rmaData.quantity,
          date: new Date(),
        });
        setItemDisabled(true);
        setLotDisabled(!!lotOpt);
      } else {
        reset({
          rma: null,
          lot: null,
          quantity: 0,
          date: new Date(),
        });
        setItemDisabled(false);
        setLotDisabled(false);
      }
    }
  }, [open, rmaData, reset]);

  const {
    data: rmaResponse,
    isLoading: rmaLoading,
    error: rmaError,
  } = useTSQDynamicList({
    page: -1,
    page_size: -1,
    primary: "returned_materials",
    alt_name: "rma",
    primary_fields: ["id", "date_opened", "number", "quantity"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "catalog_name", "name", "number"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "inventory_lots",
        alt_name: "lot",
        fields: ["id", "lot_number", "item_id"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "lot_id",
          local_field: "id",
        },
        outer: true,
      },
    ],
    filters: [
      [
        {
          table: "returned_materials",
          alt_name: "rma",
          field: "status",
          operator: "==",
          value: ["AWAITING_RECEIPT"],
          is_not: false,
          case_insensitive: false,
          inclusive_end: false,
          inclusive_start: false,
        },
      ],
    ],
    sorts: [
      {
        table: "returned_materials",
        alt_name: "rma",
        field: "date_opened",
        asc: true,
      },
    ],
    subqueries: [],
  });
  const RMA_OPTIONS: RMAOption[] = useMemo(() => {
    if (rmaResponse?.items) {
      return rmaResponse.items.map((val) => ({
        label: `${new Date(val["rma.date_opened"]).getFullYear().toString().slice(2)}-${val["rma.number"].toString().padStart(4, "0")}`,
        type: "rma_record",
        value: {
          id: val["rma.id"],
          item: {
            label: `${val["item.catalog_name"]} - ${val["item.name"]}`,
            type: "item",
            value: {
              id: val["item.id"],
              number: val["item.number"],
              name: val["item.name"],
              catalog: val["item.catalog_name"],
            },
          },
          lot: !!val["lot.id"]
            ? {
                label: val["lot.lot_number"],
                type: "lot",
                value: {
                  id: val["lot.id"],
                  item_id: val["lot.item_id"],
                },
              }
            : null,
          quantity: val["rma.quantity"],
        },
      }));
    }
    return [];
  }, [rmaResponse]);

  const {
    data: itemResponse,
    isLoading: itemLoading,
    error: itemError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "items",
      alt_name: "item",
      primary_fields: ["id", "catalog_name", "number", "name"],
      joins: [],
      filters: [
        [
          {
            table: "items",
            alt_name: "item",
            field: "catalog_name",
            operator: "==",
            value: null,
            is_not: true,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
          },
        ],
      ],
      sorts: [
        {
          table: "items",
          alt_name: "item",
          field: "catalog_name",
          asc: true,
        },
      ],
      subqueries: [],
    },
    open && !itemDisabled,
  );
  const ITEM_OPTIONS: RMAItemOption[] = useMemo(() => {
    if (itemResponse?.items) {
      return itemResponse.items.map((val) => ({
        label: `${val["item.catalog_name"]}-${val["item.name"]}`,
        type: "item",
        value: {
          id: val["item.id"],
          number: val["item.number"],
          name: val["item.name"],
          catalog: val["item.catalog_name"],
        },
      }));
    }
    return [];
  }, [itemResponse]);

  const {
    data: lotResponse,
    isLoading: lotLoading,
    error: lotError,
  } = useTSQDynamicList(
    {
      page: -1,
      page_size: -1,
      primary: "inventory_lots",
      alt_name: "lot",
      primary_fields: ["id", "lot_number", "item_id"],
      joins: [],
      filters: [
        [
          {
            table: "inventory_lots",
            alt_name: "lot",
            field: "item_id",
            operator: "==",
            value: watchedItemOpt?.value.id || null,
            is_not: false,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
          },
        ],
      ],
      sorts: [
        {
          table: "inventory_lots",
          alt_name: "lot",
          field: "lot_number",
          asc: true,
        },
      ],
      subqueries: [],
    },
    open && !!watchedItemOpt && !lotDisabled,
  );
  const LOT_OPTIONS: RMALotOption[] = useMemo(() => {
    if (lotResponse?.items) {
      return lotResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        value: {
          id: val["lot.id"],
          item_id: val["lot.item_id"],
        },
      }));
    }
    return [];
  }, [lotResponse]);

  const onSubmit = async (data: RMAFormDataReceive) => {
    try {
      setError(null);
      setSuccess(null);
      console.log(data);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to receive RMAs");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <RMADialogShell
      open={open}
      maxWidth={"sm"}
      mode="receive"
      isSubmitting={isSubmitting}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      debugValues={getValues()}
      debugErrors={errors}
      actions={
        <>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit, console.error)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Receiving..." : "Receive RMA"}
          </Button>
        </>
      }
      user={user}
    >
      <Stack spacing={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Controller
            name={`rma`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <Autocomplete
                value={value}
                onChange={(e, v) => {
                  onChange(v);
                  if (!!v) {
                    setValue("item", v.value.item, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setItemDisabled(true);
                    setValue("lot", v.value.lot, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setLotDisabled(!!v.value.lot);
                    setValue("quantity", v.value.quantity, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  } else {
                    setValue("item", null, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setItemDisabled(false);
                    setValue("lot", null, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setLotDisabled(false);
                    setValue("quantity", 0, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }
                }}
                loading={rmaLoading}
                options={RMA_OPTIONS}
                getOptionLabel={(opt) => opt?.label || ""}
                renderInput={(params) => (
                  <TextField
                    label="RMA"
                    {...params}
                    error={!!errors?.rma}
                    helperText={errors?.rma?.message}
                  />
                )}
                fullWidth
                {...restField}
              />
            )}
          />
          <Box display="flex" gap={1}>
            <Controller
              name={`item`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  value={value}
                  onChange={(e, v) => {
                    onChange(v);
                    if (
                      watchedRmaOpt?.value.item.value.id !== v?.value.id ||
                      watchedLotOpt?.value.item_id !== v?.value.id
                    ) {
                      setValue("lot", null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      setLotDisabled(false);
                      setValue("quantity", 0, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }
                  }}
                  loading={itemLoading}
                  disabled={itemDisabled}
                  options={ITEM_OPTIONS}
                  getOptionLabel={(opt) => opt?.label || ""}
                  renderInput={(params) => {
                    params.InputProps.endAdornment = itemDisabled ? (
                      <Box display="flex">
                        <Tooltip title="Change RMA Item">
                          <IconButton
                            onClick={() => {
                              setValue("item", null, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                              setItemDisabled(false);
                              setValue("lot", null, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                              setLotDisabled(false);
                              setValue("quantity", 0, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      params.InputProps.endAdornment
                    );
                    return (
                      <TextField
                        label="Item"
                        {...params}
                        error={!!errors?.item}
                        helperText={errors?.item?.message}
                      />
                    );
                  }}
                  fullWidth
                  {...restField}
                />
              )}
            />
            <Controller
              name={`lot`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  value={value}
                  onChange={(e, v) => {
                    onChange(v);
                  }}
                  loading={lotLoading}
                  options={LOT_OPTIONS}
                  disabled={lotDisabled}
                  getOptionLabel={(opt) => opt?.label || ""}
                  renderInput={(params) => {
                    params.InputProps.endAdornment = lotDisabled ? (
                      <Box display="flex">
                        <Tooltip title="Change RMA Lot">
                          <IconButton
                            onClick={() => {
                              setValue("lot", null, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                              setLotDisabled(false);
                              setValue("quantity", 0, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      params.InputProps.endAdornment
                    );
                    return (
                      <TextField
                        label="Lot"
                        {...params}
                        error={!!errors?.lot}
                        helperText={errors?.lot?.message}
                      />
                    );
                  }}
                  fullWidth
                  {...restField}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={1}>
            <Controller
              name={"date"}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <DatePicker
                  label="Date Received"
                  value={value}
                  onChange={onChange}
                  {...restField}
                  slotProps={{
                    textField: {
                      error: !!errors.date,
                      helperText: errors.date?.message,
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="quantity"
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <NumericEntryField
                  value={value}
                  onChange={(v) => onChange(v)}
                  decimalPlaces={0}
                  isCurrency={false}
                  textFieldProps={{ fullWidth: true }}
                />
              )}
            />
          </Box>
        </LocalizationProvider>
      </Stack>
    </RMADialogShell>
  );
};

const RMAViewForm: React.FC<RMAModeFormProps> = ({
  open,
  rmaData,
  user,
  onClose,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [ProgressStatus, InitAddr, ReturnAddr]: [
    ProgressStatus: ProgressIndicatorProps | undefined,
    InitAddr: string | undefined,
    ReturnAddr: string | null | undefined,
  ] = React.useMemo(() => {
    if (rmaData) {
      const ProgressStatus: ProgressIndicatorProps = {
        steps: [
          {
            label: "Confirmation",
          },
          {
            label: "Receipt",
          },
          {
            label: "Inspection",
          },
          {
            label: "Service",
          },
          {
            label: "Review",
          },
          {
            label: "Disposition",
          },
        ],
        activeStep: (() => {
          switch (rmaData.status) {
            case "AWAITING_CENTESE_CONFIRMATION":
            case "AWAITING_CUSTOMER_CONFIRMATION":
              return 0;
            case "AWAITING_RECEIPT":
              return 1;
            case "AWAITING_INSPECTION":
            case "INSPECTION_IN_PROGRESS":
              return 2;
            case "AWAITING_REPAIR":
            case "REPAIRS_IN_PROGRESS":
              return 3;
            case "AWAITING_REVIEW":
              return 4;
            case "AWAITING_SHIPMENT":
              return 5;
            case "COMPLETED":
              return 6;
          }
        })(),
        statusOverride: rmaData.is_voided
          ? "void"
          : rmaData.is_holding
            ? "hold"
            : undefined,
      };
      let InitAddr = "";
      if (rmaData.address.attn)
        InitAddr += `Attention: ${rmaData.address.attn}\n`;
      if (rmaData.address.addressee)
        InitAddr += `Addressee: ${rmaData.address.addressee}\n`;
      InitAddr += `${rmaData.address.line_1}\n`;
      if (rmaData.address.line_2) InitAddr += `${rmaData.address.line_2}\n`;
      if (rmaData.address.line_3) InitAddr += `${rmaData.address.line_3}\n`;
      InitAddr += `${rmaData.address.city}, ${rmaData.address.state}\n`;
      InitAddr += `${rmaData.address.country}, ${rmaData.address.zip}\n`;
      if (rmaData.address.phone) InitAddr += `${rmaData.address.phone}\n`;
      if (rmaData.address.email) InitAddr += `${rmaData.address.email}`;
      let ReturnAddr = null;
      if (rmaData.return_addr) {
        ReturnAddr = "";
        if (rmaData.return_addr.attn)
          ReturnAddr += `Attention: ${rmaData.return_addr.attn}\n`;
        if (rmaData.return_addr.addressee)
          ReturnAddr += `Addressee: ${rmaData.return_addr.addressee}\n`;
        ReturnAddr += `${rmaData.return_addr.line_1}\n`;
        if (rmaData.return_addr.line_2)
          ReturnAddr += `${rmaData.return_addr.line_2}\n`;
        if (rmaData.return_addr.line_3)
          ReturnAddr += `${rmaData.return_addr.line_3}\n`;
        ReturnAddr += `${rmaData.return_addr.city}, ${rmaData.return_addr.state}\n`;
        ReturnAddr += `${rmaData.return_addr.country}, ${rmaData.return_addr.zip}\n`;
        if (rmaData.return_addr.phone)
          ReturnAddr += `${rmaData.return_addr.phone}\n`;
        if (rmaData.return_addr.email)
          ReturnAddr += `${rmaData.return_addr.email}`;
      }
      if (rmaData.return_addr) return [ProgressStatus, InitAddr, ReturnAddr];
    }
    return [undefined, undefined, undefined];
  }, [rmaData]);

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <RMADialogShell
      open={open}
      mode="view"
      isSubmitting={false}
      error={error}
      success={success}
      onClearError={() => setError(null)}
      onClose={handleClose}
      actions={
        <>
          <Button onClick={handleClose}>Close</Button>
        </>
      }
      user={user}
    >
      {!rmaData ? (
        <Box display="flex">
          <Typography variant="h3" align="center">
            RMA Data Invalid. Close dialog and try again. Notify IT if issue
            persists.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {!!ProgressStatus && (
            <Box display="flex" width="100%" justifyContent="center">
              <ProgressIndicator {...ProgressStatus} />
            </Box>
          )}
          <Typography variant="h3" align="center">
            Basic Information
          </Typography>
          <Box
            display="flex"
            gap={1}
            width="100%"
            justifyContent={"space-around"}
          >
            <Card>
              <CardContent>
                <Stack direction="column" spacing={1} width="100%">
                  <Typography variant="body1">
                    RMA:{" "}
                    {`${rmaData.date_opened.getFullYear().toString().slice(2)}-${rmaData.number.toString().padStart(4, "0")}`}
                  </Typography>
                  {rmaData.nuc_cf_num ? (
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {rmaData.nuc_cf_num}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      No CF Linked
                    </Typography>
                  )}
                  {rmaData.nuc_cf_num && (
                    <>
                      {rmaData.gl_cf ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary" }}
                        >
                          GL: {rmaData.gl_cf}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary" }}
                        >
                          No GL CF Number
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Stack direction="column" spacing={1} width="100%">
                  <Typography variant="body1">
                    Opened: {rmaData.date_opened.toLocaleDateString("en-US")}
                  </Typography>
                  <Typography variant="body1">
                    Received:{" "}
                    {rmaData.date_received
                      ? rmaData.date_received.toLocaleDateString("en-US")
                      : "N/A"}
                  </Typography>
                  <Typography variant="body1">
                    Closed:{" "}
                    {rmaData.date_closed
                      ? rmaData.date_closed.toLocaleDateString("en-US")
                      : "N/A"}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Stack direction="column" spacing={1}>
                  <Typography variant="body1">
                    {rmaData.cust_code} {rmaData.cust_name}
                  </Typography>
                  <Typography variant="body1">
                    {rmaData.contact.name}
                    <br />
                    {rmaData.contact.email ? (
                      <a
                        href={`mailto:${rmaData.contact.email}`}
                        color={"inherit"}
                        rel={"noreferrer"}
                        target={"_blank"}
                      >
                        {rmaData.contact.email}
                      </a>
                    ) : (
                      "No Email Provided"
                    )}
                    <br />
                    {rmaData.contact.phone
                      ? rmaData.contact.phone
                      : "No Phone Provided"}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          <Typography variant="h3" align="center">
            Address Information
          </Typography>
          <Box
            display="flex"
            gap={1}
            width="100%"
            justifyContent={"space-around"}
          >
            <Stack direction="column" spacing={0.5} minWidth="45%">
              <Typography variant="h4">Initial Address</Typography>
              <TextField
                value={InitAddr || "NO ADDRESS PROVIDED"}
                multiline
                rows={5}
                fullWidth
                slotProps={{ input: { readOnly: true } }}
              />
            </Stack>
            <Stack direction="column" spacing={0.5} minWidth="45%">
              <Typography variant="h4">Return Address</Typography>
              {rmaData.return_addr ? (
                <TextField
                  value={ReturnAddr || "NO ADDRESS PROVIDED"}
                  multiline
                  rows={5}
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                />
              ) : (
                <Typography variant="h4" align="center">
                  Returning Serviced or Replacement Product to Initial Address
                  upon RMA Completion
                </Typography>
              )}
            </Stack>
          </Box>
          <Typography variant="h3" align="center">
            RMA Details
          </Typography>
          <Box
            display="flex"
            width="100%"
            gap={1}
            justifyContent={"space-around"}
          >
            <Stack direction="column" spacing={0.5}>
              <Typography variant="h4">Returned Lot</Typography>
              <Typography variant="body1">{rmaData.item_catalog}</Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.primary" }}
              >{`${rmaData.quantity}x ${rmaData.lot_num ? rmaData.lot_num : "UNKNOWN LOT"}`}</Typography>
              {rmaData.quantity_received && (
                <Typography variant="body2">
                  Received: {rmaData.quantity_received}x
                </Typography>
              )}
            </Stack>
            <Stack direction="column" spacing={0.5}>
              <Typography variant="h4">Loaner</Typography>
              {rmaData.loaner_num ? (
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {rmaData.loaner_num}
                  </Typography>
                  <Typography variant="body2">
                    Shipment Status:{" "}
                    {rmaData.shipment_loaner_status
                      ? stringUtils.capFirstLetter(
                          rmaData.shipment_loaner_status.replaceAll("_", " "),
                        )
                      : "UNKNOWN"}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body1" align="center">
                  N/A
                </Typography>
              )}
            </Stack>
            <Stack direction="column" spacing={0.5}>
              <Typography variant="h4">Replacement</Typography>
              {rmaData.replacement_num ? (
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {rmaData.replacement_num}
                  </Typography>
                  <Typography variant="body2">
                    Shipment Status:{" "}
                    {rmaData.shipment_return_status
                      ? stringUtils.capFirstLetter(
                          rmaData.shipment_return_status.replaceAll("_", " "),
                        )
                      : "UNKNOWN"}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body1" align="center">
                  N/A
                </Typography>
              )}
            </Stack>
          </Box>
          <Stack
            direction="column"
            spacing={0.5}
            justifyContent={"space-around"}
          >
            <Typography variant="body1">
              Return Reason:{" "}
              {rmaData.reason === "OTHER"
                ? rmaData.reason_other
                : stringUtils.capFirstLetter(
                    rmaData.reason.replaceAll("_", " "),
                  )}
            </Typography>
            <TextField
              value={rmaData.rma_details}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
          <Typography variant="h3" align="center">
            Service Details
          </Typography>
          <Typography variant="h4" align="center">
            SERVICE MODULE UNDER CONSTRUCTION
          </Typography>
        </Stack>
      )}
    </RMADialogShell>
  );
};

export interface RMAModeFormProps {
  open: boolean;
  rmaData: RMA_FULL | null;
  user: AuthUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface RMAHandlerFormProps extends RMAModeFormProps {
  mode: RMAFormMode;
}

const RMAHandlerForm: React.FC<RMAHandlerFormProps> = ({ mode, ...rest }) => {
  switch (mode) {
    case "create":
      return <RMACreateForm {...rest} />;
    case "createbatch":
      return <RMABatchCreateForm {...rest} />;
    case "edit":
      return <RMAEditForm {...rest} />;
    case "receive":
      return <RMAReceiveForm {...rest} />;
    case "receivebatch":
      return <RMAReceiveBatchForm {...rest} />;
    case "view":
      return <RMAViewForm {...rest} />;
    default:
      return null;
  }
};

export default RMAHandlerForm;
